/**
 * assets/dm-nav-drawer.js — the mobile nav drawer's behaviour layer.
 *
 * CANDIDATE COMPONENT. `snippets/dm-nav-drawer.liquid` is derived, not
 * transcribed (06 §5.2 says the drawer "does not exist in any prototype");
 * this script is the same status. See the header comment in that snippet and
 * `docs/design-decisions.md` DD-10 for the full list of judgement calls.
 *
 * WHY THIS FILE EXISTS AT ALL. The brief for this component prefers a
 * zero-JS `<details>` solution and gets exactly that for open/close
 * *visibility* — the panel's `[open]` attribute alone shows and hides it,
 * no script required. What `<details>` cannot do on its own is the
 * mandatory a11y contract: a focus trap while open, background scroll lock,
 * and returning focus to the toggle on close. Those three are genuinely not
 * achievable in CSS/HTML, so this file exists to add exactly them and
 * nothing else.
 *
 * REUSE, NOT REBUILD. `trapFocus` / `removeTrapFocus` are Dawn's OWN retained
 * a11y helpers (assets/global.js, loaded earlier in document order — see
 * dm-core.js's header comment on script ordering). This file calls them
 * rather than reimplementing a focus trap. `<details-modal>` (also retained,
 * assets/details-modal.js) was considered and rejected: its `open()` is
 * hardcoded to focus the panel's first `input:not([type="hidden"])`, which a
 * nav list does not have — calling `.focus()` on the `null` that querySelector
 * would return throws. A custom element on the `DMElement` base (dm-core.js)
 * is the documented escape hatch for exactly this case.
 *
 * Scroll lock reuses `.overflow-hidden`, the same class + rule
 * `assets/cart-drawer.js`, `assets/details-modal.js` and Dawn's own
 * `MenuDrawer` already use (assets/dm-base.css, "G9 · A11y & visibility
 * state" — do not rewrite that class, its doc comment explains why).
 *
 * Classic script, ES2020, no dependencies beyond the two globals above.
 */
(function () {
  'use strict';

  class DMNavDrawer extends DMElement {
    init() {
      this.details = this.querySelector(':scope > details');
      if (!this.details) return;

      this.toggle = this.details.querySelector(':scope > summary');
      this.panel = this.details.querySelector(':scope > [role="dialog"]');
      this.closeButton = this.panel ? this.panel.querySelector('.dm-nav-drawer__close') : null;
      if (!this.toggle || !this.panel) return;

      // The single source of truth is the `<details>` element's own `open`
      // state — every entry point below (summary click, close button, Esc)
      // sets `this.details.open` and lets the native `toggle` event drive
      // the a11y side effects. Nothing here reaches for `[open]` directly.
      this.on(this.details, 'toggle', () => {
        if (this.details.open) this.onOpen();
        else this.onClose();
      });

      if (this.closeButton) {
        this.on(this.closeButton, 'click', () => {
          this.details.open = false;
        });
      }

      // Esc anywhere inside the open drawer closes it. `trapFocus` already
      // keeps Tab inside the panel; this is the one key it does not own.
      this.on(this.details, 'keydown', (event) => {
        if (event.key !== 'Escape' && event.key !== 'Esc') return;
        if (!this.details.open) return;
        event.preventDefault();
        this.details.open = false;
      });

      // A resize into desktop width while the drawer is open (rotation, an
      // attached keyboard, a responsive-mode devtools drag) leaves a
      // scroll-locked page with no visible way to close it if `dm-header`
      // hides the toggle at that width. Belt, not a design decision.
      this.desktopQuery = window.matchMedia('(min-width: 750px)');
      this.on(this.desktopQuery, 'change', (event) => {
        if (event.matches && this.details.open) this.details.open = false;
      });
    }

    onOpen() {
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      trapFocus(this.panel, this.closeButton || this.panel);
    }

    onClose() {
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.toggle);

      // Collapse any expanded child accordions so the drawer reopens clean
      // rather than remembering scroll position inside a stale submenu.
      //
      // Removing `[open]` is the WHOLE fix. An earlier version also stamped
      // `aria-expanded="false"` on each child `<summary>`, which was worse than
      // doing nothing: the markup declares no `aria-expanded` on those
      // summaries, and nothing ever set it back to `true` on re-expand. So
      // after the first open/close cycle every submenu carried a permanent
      // `aria-expanded="false"` that an explicit ARIA attribute makes
      // AUTHORITATIVE OVER the native `<details>` state — a screen reader would
      // announce "collapsed" on a visibly open submenu, forever.
      //
      // `<summary>` already exposes expanded/collapsed natively, which is the
      // entire reason this component is built on `<details>`. The correct
      // number of aria-expanded writes on a child summary is zero. (The
      // drawer's own toggle is different: it declares `aria-expanded` in the
      // markup and onOpen/onClose keep both halves in sync.)
      this.querySelectorAll('.dm-nav-drawer__subdetails[open]').forEach((node) => {
        node.removeAttribute('open');
      });
    }

    // Belt: a theme-editor subtree swap while the drawer happens to be open
    // must not stand a scroll lock or a document-level trapFocus listener
    // pointed at a node that is about to be removed.
    destroy() {
      if (this.details && this.details.open) {
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus();
      }
    }
  }

  DMElement.define('dm-nav-drawer', DMNavDrawer);
})();
